import type { CreatorTag as CreatorTagType } from "@unbox/api";
import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { readableTextColor } from "@/lib/creator-tags/colors";

// Pills truncate long labels; the full name (and description) live in the tooltip.
const LABEL_MAX_CHARS = 16;
const DESCRIPTION_MAX_CHARS = 120;

const truncate = (value: string, max: number) => (value.length > max ? `${value.slice(0, max).trimEnd()}…` : value);

type PillTag = Pick<CreatorTagType, "name" | "color"> & { description?: string | null };

export const CreatorTag = ({
  tag,
  truncateLabel = true,
  showTooltip = true,
  iconTrailing,
}: {
  tag: PillTag;
  /** Truncate the label to ~16 chars. Off inside the overflow popover, which has room for full names. */
  truncateLabel?: boolean;
  /** Wrap the pill in a hover tooltip showing the full name + description. */
  showTooltip?: boolean;
  iconTrailing?: ReactNode;
}) => {
  const label = truncateLabel ? truncate(tag.name, LABEL_MAX_CHARS) : tag.name;

  const pill = (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary px-2.5 py-0.5 text-xs font-semibold"
      style={{
        backgroundColor: `color-mix(in srgb, ${tag.color} 20%, transparent)`,
        color: readableTextColor(tag.color),
      }}
    >
      {label}
      {iconTrailing}
    </span>
  );

  if (!showTooltip) return pill;

  const description = tag.description?.trim();

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent className="max-w-[260px]">
          <p className="font-medium">{tag.name}</p>
          {description && <p className="mt-0.5 text-tertiary">{truncate(description, DESCRIPTION_MAX_CHARS)}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
